import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { UirouterMigrationService } from 'Utilities/uirouter.migration.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/Subject';
import { finalize, takeUntil } from 'rxjs/operators';


import { ChainService } from './chain.service';
import { Employee } from './employee.model';
import { Chain } from './chain.model';

@Component({
  selector: 'app-chains-edit',
  template: require('./chains.edit.component.html'),
})

/** @desc Container component to handle editing of chains **/
export class ChainsEditComponent implements OnInit, OnDestroy {
  isLoading = false;
  chainId: number;
  chain = null;
  employees: Employee[];
  newMerchantId: number;
  addMerchantForm: FormGroup;
  ngUnsubscribe: Subject<any> = new Subject();

  constructor(
    private uirouterMigrationService: UirouterMigrationService,
    private chainService: ChainService,
    private notificationService: ToastrService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    this.createAddMerchantForm();
    this.uirouterMigrationService.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe(hash => {
      this.chainId = this.uirouterMigrationService.getParams('app.chains-edit', hash).chainId;
      if (!isNaN(this.chainId)) {
        this.getChain();
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * @desc Function to create form for adding merchants
   * @return {void}
   */
  createAddMerchantForm(): void {
    this.addMerchantForm = this.fb.group({
      merchantId: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(80)],
      }),
    });

    // TODO remove when no hybrid app
    this.addMerchantForm.get('merchantId').valueChanges.subscribe(() => this.cd.detectChanges());
  }

  /**
   * @desc Function to retrieve chain by given id
   * @return {void}
   */
  getChain(): void {
    this.enableLoading();
    this.chainService
      .getChainById(this.chainId)
      .pipe(finalize(() => {
        this.disableLoading();
        this.cd.detectChanges();
      }))
      .subscribe((res: Chain) => {
        this.initChain(res);
        this.getEmployeesInChain();
      });
  }

  /**
   * @desc Function to retrieve employees of given chain
   * @return {void}
   */
  getEmployeesInChain(): void {
    this.enableLoading();
    this.chainService
      .getEmployeesFromChain(this.chainId)
      .pipe(finalize(() => {
        this.disableLoading();
        this.cd.detectChanges();
      }))
      .subscribe((res: { employees: Array<Employee> }) => {
        if (res.employees.length) {
          this.employees = res.employees;
        }
      });
  }

  /**
   * @desc Function to toggle the access to the chain center for given employee
   * @param {Employee} employee - The employee object that needs to get updated
   * @return {void}
   */
  allowEmployeeToLogInChain(employee: Employee): void {
    this.enableLoading();
    employee.hasAccessToChainCenter = !employee.hasAccessToChainCenter;
    this.chainService
      .updateEmployeeAccess(this.chainId, employee)
      .pipe(finalize(() => {
        this.getEmployeesInChain();
      }))
      .subscribe(() => {
        const notif = employee.hasAccessToChainCenter ? 'This employee is now able to login to the chains center' : 'access removed';
        this.notificationService.success(notif);
      });
  }

  /**
   * @desc Function to handle the initalization of restaurant data of a chain
   * @param {Chain} chain - The chain-data to be initialized
   * @return {void}
   */
  initChain(chain: Chain): void {
    let i, len;
    this.chain = chain;
    this.chain.restaurants.map(restaurant => {
      const destinationTree = [];
      for (i = 1, len = restaurant.destination.tree.length; i < len; i++) {
        destinationTree.push(restaurant.destination.tree[i].name);
      }
      restaurant.destinationTree = destinationTree.join(', ');
    });
  }

  /**
   * @desc Function to delete a chain by it's id
   * @return {void}
   */
  deleteChain(): void {
    if (this.isLoading) {
      return;
    }
    if (window.confirm('Are you sure you want to delete this chain?')) {
      this.enableLoading();
      this.chainService
        .deleteChain(this.chainId)
        .pipe(finalize(() => {
          this.disableLoading();
          this.cd.detectChanges();
        }))
        .subscribe(
          () => {
            this.notificationService.success('Chain deleted');
            this.uirouterMigrationService.goTo('app.chains');
          },
          res => {
            const errorMessage = res.data.message ? res.data.message : 'Deleting chain failed';
            this.notificationService.error(errorMessage);
          },
        );
    }
  }

  triggerChange(): void {
    setTimeout(() => this.cd.detectChanges(), 0); // TODO remove when no hybrid app
  }

  /**
   * @desc Function to add a merchant to the opened chain by form input
   * @return {void}
   */
  addMerchantToChain(): void {
    if (this.isLoading) {
      return;
    }
    this.enableLoading();
    this.chainService
      .addMerchantToChain(this.chainId, this.addMerchantForm.value.merchantId)
      .pipe(finalize(() => {
        this.disableLoading();
        this.cd.detectChanges();
      }))
      .subscribe(
        () => {
          this.getChain();
          this.addMerchantForm.get('merchantId').reset();
          this.notificationService.success('Restaurant added to chain');
        },
        res => {
          const error = Array.isArray(res.error.errors) ? res.error.errors.shift() : res.error;
          const errorMessage = error.message ? error.message : 'Restaurant addition to chain failed';
          this.translate.get(errorMessage).subscribe((resp: string) => {
            this.notificationService.error(resp);
          });
        },
      );
  }

  /**
   * @desc Function to remove a merchant from the opened chain
   * @param {number} merchantId - Id of the merchant to be removed
   * @param {string} merchantName - Name of the merchant needed for confirmation dialog
   * @return {void}
   */
  removeMerchantFromChain(merchantId: number, merchantName: string): void {
    if (this.isLoading) {
      return;
    }
    if (window.confirm('Are you sure you want to remove ' + merchantName + ' from the chain?')) {
      this.enableLoading();
      this.chainService
        .removeMerchantFromChain(this.chainId, merchantId)
        .pipe(finalize(() => {
          this.disableLoading();
          this.cd.detectChanges();
        }))
        .subscribe(
          () => {
            this.getChain();
            this.notificationService.success('Restaurant removed from chain');
          },
          () => {
            this.notificationService.error('Restaurant removal from chain failed');
          },
        );
    }
  }

  /**
   * @desc Helper function to enable loading
   * @return {void}
   */
  enableLoading(): void {
    this.isLoading = true;
  }

  /**
   * @desc Helper function to disable loading
   * @return {void}
   */
  disableLoading(): void {
    this.isLoading = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/chains/chains.edit.component.ts